import {
  Box,
  Button,
  Divider,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Skeleton,
  Text,
  useToast,
} from '@chakra-ui/react'
import { Search, TriangleAlert } from 'lucide-react'
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, useEffect } from 'react'
import { createDocumentFromTemplate, getDocuments } from '../../api/document/document'
import ModalInput from '../forms/modalForms/modalInput'
import handleToast from '../HandleToast'
import ModalSelect from '../forms/modalForms/modalSelect'
import PillToggleGroup from '../forms/modalForms/pillToggleGroup'
import { useTranslation } from 'react-i18next'
import { modal, button } from '../../themes/styles'

import { DocumentType } from '../../types/document/documentTypes'
import DocumentCard from '../cards/DocumentCard'
import { fetchShortEmployeeList, fetchTeamList } from '../../api/workforce/workforce'
import { fetchJobList } from '../../api/classification/classification'

interface SelectedUser {
  id: string
  first_name: string
  last_name: string
  profile_picture: string
}

interface AddDocumentFromTemplateModalProps {
  isOpen: boolean
  onClose: () => void
  docType?: 'templates' | 'documents'  
  onSuccess?: () => void 
}

const AddDocumentFromTemplateModal: React.FC<AddDocumentFromTemplateModalProps> = ({ isOpen, onClose, docType = 'documents', onSuccess }) => {
  const { t } = useTranslation()
  const toast = useToast()
  const queryClient = useQueryClient()

  const [templateSearch, setTemplateSearch] = useState('')
  const [userSearch, setUserSearch] = useState('')
  const [teams, setTeams] = useState<Array<{ id: string; name: string }>>([])
  const [jobProfiles, setJobProfiles] = useState<Array<{ id: string; title: string }>>([])
  const [data, setData] = useState({
    fileName: '',
    signingStatus: '',
    users: [] as SelectedUser[],
    template: null as string | null,
    recipientType: 'user' as 'user' | 'all' | 'job_profile' | 'team',
    selectedTeam: '',
    selectedJobProfile: '',
  })

  const [errors, setErrors] = useState({
    fileName: false,
    recipient: false,
    template: false,
  })

  const templates = useQuery({
    queryKey: ['documentFromTemplate', templateSearch],
    queryFn: () =>
      getDocuments({
        search: templateSearch,
        page: 1,
        page_size: 20,
        doc_type: 'templates',
        // minimal set for template picker
        fields: 'id,title,file,created_at,author.first_name,author.last_name,author.profile_picture,status',
      }),
    enabled: isOpen, 
  })

  const recipientUsersQuery = useInfiniteQuery({
    queryKey: ['documentFromTemplate-recipients-users', userSearch],
    queryFn: ({ pageParam }) => fetchShortEmployeeList({ search: userSearch, page: pageParam, pageSize: 10 }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => (lastPage?.has_next ? allPages.length + 1 : undefined),
    enabled: isOpen && data.recipientType === 'user',
  })

  const recipientUserList: SelectedUser[] = (recipientUsersQuery.data?.pages ?? []).flatMap((p: { data?: SelectedUser[] }) =>
    Array.isArray(p?.data) ? p.data : [],
  )

  const selectedRecipientUser = data.users[0]
  const recipientUserOptions = (() => {
    const options = recipientUserList.map((u) => ({
      value: u.id,
      label: `${u.first_name} ${u.last_name}`.trim(),
      profilePicture: u.profile_picture,
    }))

    if (selectedRecipientUser && !options.some((o) => o.value === selectedRecipientUser.id)) {
      options.unshift({
        value: selectedRecipientUser.id,
        label: `${selectedRecipientUser.first_name} ${selectedRecipientUser.last_name}`.trim(),
        profilePicture: selectedRecipientUser.profile_picture,
      })
    }

    return options
  })()

  useEffect(() => {
    if (isOpen) {
      queryClient.invalidateQueries({ queryKey: ['documentFromTemplate'] })
      // Load teams and job profiles
      const loadData = async () => {
        try {
          const [teamsData, jobsData] = await Promise.all([
            fetchTeamList({ search: '', page: 1, pageSize: 100, filters: {} }),
            fetchJobList({ search: '', page: 1, pageSize: 100 })
          ])
          setTeams(teamsData?.data || [])
          setJobProfiles(jobsData?.data || [])
        } catch (error) {
          console.error('Error loading teams/jobs:', error)
        }
      }
      loadData()
    }
  }, [isOpen, queryClient])

  // Prefetch next page of templates
  useEffect(() => {
    if (!isOpen) return
    const nextPage = 2
    // Only prefetch if server indicates more pages
    if (templates.data?.max_page && templates.data.max_page <= 1) return
    try {
      queryClient.prefetchQuery({
        queryKey: ['documentFromTemplate', templateSearch, nextPage],
        queryFn: () =>
          getDocuments({
            search: templateSearch,
            page: nextPage,
            page_size: 20,
            doc_type: 'templates',
            fields: 'id,title,file,created_at,author.first_name,author.last_name,author.profile_picture,status',
          }),
        staleTime: 30 * 1000,
      })
    } catch (err) {
      // ignore
    }
  }, [isOpen, templateSearch, queryClient])

  const createDocumentMutation = useMutation({
    mutationFn: createDocumentFromTemplate,
    onSuccess: () => {
      handleToast(toast, true, t('documents.toasts.createdTitle'), t('documents.toasts.createdDescription'))
      queryClient.invalidateQueries({ queryKey: ['documents'] })
      handleClose()
    },
    onError: (error: Error & { response?: { data?: { recipients?: string; detail?: string; message?: string } } }) => {
      const errorMessage = error?.response?.data?.recipients ||
                          error?.response?.data?.detail ||
                          error?.response?.data?.message ||
                          error.message ||
                          t('common.states.error')
      handleToast(toast, false, t('documents.toasts.uploadFailedTitle'), errorMessage)
    },
  })

  const validateData = () => {
    let recipientError = false
    
    if (data.recipientType === 'user' && data.users.length === 0) {
      recipientError = true
    } else if (data.recipientType === 'team' && !data.selectedTeam) {
      recipientError = true
    } else if (data.recipientType === 'job_profile' && !data.selectedJobProfile) {
      recipientError = true
    }
    
    const errors = {
      fileName: data.fileName === '',
      recipient: recipientError,
      template: !data.template,
    }

    setErrors(errors)

    return !Object.values(errors).some((error) => error)
  }
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setData((prev) => ({ ...prev, [name]: value }))
    setErrors({ ...errors, [name]: false })
  }

  const handleSubmit = async () => {
    try {
      if (!validateData()) return
      
      const payload: Parameters<typeof createDocumentFromTemplate>[0] = {
        template: data.template as string,
        title: data.fileName,
        signing_required: data.signingStatus === 'pending_signing',
        digital_signing_required: data.signingStatus === 'pending_digital_signature',
        recipient_type: data.recipientType,
      }
      
      // Add appropriate recipient data based on type
      if (data.recipientType === 'user') {
        payload.recipients = data.users.map((user) => user.id)
      } else if (data.recipientType === 'team') {
        payload.recipient_team = data.selectedTeam
      } else if (data.recipientType === 'job_profile') {
        payload.recipient_job_profile = data.selectedJobProfile
      } else if (data.recipientType === 'all') {
        payload.recipients = []
      }
      
      createDocumentMutation.mutate(payload)
      
      if (onSuccess && docType === 'templates') {
        onSuccess()  
      }
      
      if (docType === 'documents') {
        onClose()  
      }
    } catch {
      // submission errors are handled by onError
    }
  }
  const handleClose = () => {
    setData({
      fileName: '',
      users: [],
      template: null,
      signingStatus: '',
      recipientType: 'user',
      selectedTeam: '',
      selectedJobProfile: '',
    })
    onClose()
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} isCentered size="lg">
      <ModalOverlay />
      <ModalContent minW="900px">
        <ModalHeader {...modal.header}>{t('documents.modals.fromTemplate.title')}</ModalHeader>
        <ModalCloseButton sx={modal.closeButton} />

        <ModalBody>
          <Box width="100%" mb="16px">
            <ModalInput
              label={t('documents.fields.fileName')}
              type="text"
              name="fileName"
              placeholder={t('documents.placeholders.fileName')}
              value={data.fileName}
              onChange={handleChange}
              isInvalid={!!errors.fileName}
            />
            {errors.fileName && (
              <Flex w="100%" mt={1} alignItems="center" gap="4px">
                <TriangleAlert size={16} color="var(--chakra-colors-error-100)" />
                <Text>{t('validation.required', { field: t('documents.fields.fileName') })}</Text>
              </Flex>
            )}
          </Box>

          <Divider mb="16px" />

          <Text textStyle="h-xs" mb="16px">{t('documents.recipients.title')}</Text>

          <Box mb="16px">
            <PillToggleGroup
              label={t('documents.recipients.typeLabel')}
              options={[
                { id: 'user' as const, name: t('common.fields.user') },
                { id: 'all' as const, name: t('documents.recipients.types.all') },
                { id: 'job_profile' as const, name: t('documents.recipients.types.jobProfile') },
                { id: 'team' as const, name: t('documents.recipients.types.team') },
              ]}
              value={data.recipientType}
              onChange={(newType) => {
                setData((prev) => ({
                  ...prev,
                  recipientType: newType,
                  users: [],
                  selectedTeam: '',
                  selectedJobProfile: '',
                }))
                setErrors((prev) => ({ ...prev, recipient: false }))
              }}
            />
          </Box>

          <Flex gap="16px" mb="16px">
            {data.recipientType !== 'all' && (
              <Box flex="1">
                {data.recipientType === 'user' && (
                  <ModalSelect
                    label={t('documents.recipients.selectUsers')}
                    name="recipientsUser"
                    value={selectedRecipientUser?.id ?? ''}
                    options={[
                      ...recipientUserOptions,
                    ]}
                    onChange={(e) => {
                      const selectedId = e.target.value

                      if (!selectedId) {
                        setData((prev) => ({ ...prev, users: [] }))
                        setErrors((prev) => ({ ...prev, recipient: false }))
                        return
                      }

                      const match = recipientUserList.find((u) => String(u.id) === String(selectedId))
                      if (match) {
                        setData((prev) => ({ ...prev, users: [match] }))
                        setErrors((prev) => ({ ...prev, recipient: false }))
                        return
                      }

                      setData((prev) => ({
                        ...prev,
                        users: [{ id: selectedId, first_name: '', last_name: '', profile_picture: '' }],
                      }))
                      setErrors((prev) => ({ ...prev, recipient: false }))
                    }}
                    isInvalid={!!errors.recipient}
                    hasProfilePicture={true}
                    onSearch={(term) => setUserSearch(term)}
                    isLoading={recipientUsersQuery.isLoading}
                    onLoadMore={() => recipientUsersQuery.fetchNextPage()}
                    hasMore={!!recipientUsersQuery.hasNextPage}
                    isLoadingMore={recipientUsersQuery.isFetchingNextPage}
                  />
                )}

                {data.recipientType === 'team' && (
                  <ModalSelect
                    label={t('documents.recipients.selectTeam')}
                    name="selectedTeam"
                    value={data.selectedTeam}
                    options={[
                      { value: '', label: t('documents.recipients.placeholders.team') },
                      ...teams.map((team) => ({
                        value: team.id,
                        label: team.name,
                      })),
                    ]}
                    onChange={(e) => {
                      setData((prev) => ({ ...prev, selectedTeam: e.target.value }))
                      setErrors((prev) => ({ ...prev, recipient: false }))
                    }}
                    isInvalid={!!errors.recipient}
                  />
                )}

                {data.recipientType === 'job_profile' && (
                  <ModalSelect
                    label={t('documents.recipients.selectJobProfile')}
                    name="selectedJobProfile"
                    value={data.selectedJobProfile}
                    options={[
                      { value: '', label: t('documents.recipients.placeholders.jobProfile') },
                      ...jobProfiles.map((job) => ({
                        value: job.id,
                        label: job.title,
                      })),
                    ]}
                    onChange={(e) => {
                      setData((prev) => ({ ...prev, selectedJobProfile: e.target.value }))
                      setErrors((prev) => ({ ...prev, recipient: false }))
                    }}
                    isInvalid={!!errors.recipient}
                  />
                )}
              </Box>
            )}

            <Box flex="1">
              <ModalSelect
                label={t('paths.builder.signingType.label')}
                name="signingStatus"
                value={data.signingStatus}
                onChange={(e) => setData((prev) => ({ ...prev, signingStatus: e.target.value }))}
                options={[
                  { value: '', label: t('paths.builder.signingType.none') },
                  { value: 'pending_signing', label: t('paths.builder.signingType.requireSignature') },
                  { value: 'pending_digital_signature', label: t('paths.builder.signingType.requireDigitalSignature') },
                ]}
                searchDisabled={true}
              />
            </Box>
          </Flex>

          {errors.recipient && (
            <Flex w="100%" mt={1} alignItems="center" gap="4px">
              <TriangleAlert size={16} color="var(--chakra-colors-error-100)" />
              <Text textStyle="b-xs" color="error.100">
                {t('documents.validation.recipientRequired')}
              </Text>
            </Flex>
          )}

          <Divider my="16px" />

          <Text textStyle="h-xs" mb="16px">{t('documents.recipients.selectDocument')}</Text>
          <InputGroup>
            <InputLeftElement pointerEvents="none">
              <Search size={20} color="var(--chakra-colors-solid-400)" />
            </InputLeftElement>
            <Input
              placeholder={t('common.labels.searchPlaceholder')}
              value={templateSearch}
              onChange={(e) => setTemplateSearch(e.target.value)}
            />
          </InputGroup>

          {errors.template && (
            <Flex w="100%" mt={1} alignItems="center" gap="4px">
              <TriangleAlert size={16} color="var(--chakra-colors-error-100)" />
              <Text textStyle="b-xs" color="error.100">
                {t('documents.validation.templateRequired')}
              </Text>
            </Flex>
          )}

          <Box maxH="300px" overflowY="auto" mt="16px" pr="12px">
            {templates.isLoading && (
              <SimpleGrid minChildWidth="260px" gap="16px">
                <Skeleton h="243px" borderRadius="12px" />
                <Skeleton h="243px" borderRadius="12px" />
                <Skeleton h="243px" borderRadius="12px" />
                <Skeleton h="243px" borderRadius="12px" />
                <Skeleton h="243px" borderRadius="12px" />
                <Skeleton h="243px" borderRadius="12px" />
                <Skeleton h="243px" borderRadius="12px" />
                <Skeleton h="243px" borderRadius="12px" />
                <Skeleton h="243px" borderRadius="12px" />
                <Skeleton h="243px" borderRadius="12px" />
                <Skeleton h="243px" borderRadius="12px" />
              </SimpleGrid>
            )}
            {templates.data && (
              <SimpleGrid minChildWidth="260px" gap="16px">
                {templates.data?.data?.map(
                  (document: {
                    created_at: string
                    file: string
                    id: string
                    type: string
                    title: string
                    author: {
                      id: string
                      first_name: string
                      last_name: string
                      profile_picture: string
                      email: string
                    }
                  }) => {
                    return (
                      <DocumentCard
                        type={document.type as DocumentType}
                        title={document.title}
                        timestamp={document.created_at}
                        document={document.file}
                        author={{
                          name: `${document?.author?.first_name} ${document?.author?.last_name}`,
                          avatar: document?.author?.profile_picture,
                        }}
                        onSelect={() => setData((prev) => ({ ...prev, template: document.id }))}
                        isSelected={data.template === document.id}
                      />
                    )
                  },
                )}
              </SimpleGrid>
            )}
          </Box>
        </ModalBody>
        <ModalFooter {...modal.footer}>
          <Button {...button.secondary} onClick={handleClose}>
            {t('common.actions.cancel')}
          </Button>
          <Button {...button.primary} onClick={handleSubmit} isLoading={createDocumentMutation.isPending}>
            {t('common.actions.submit')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default AddDocumentFromTemplateModal
